/**
 * Helper functions for working with taxable value data
 * These functions provide utilities for growth calculations, validations, and projections
 * Used by TaxableValueBuilder, revenue calculations, and other components needing value projections
 */

import { TIME_PERIODS } from '~/config/financial-constants';
import type { BondSeries, TaxableValueMapEntry, TaxValueInputs, TaxValueInputsForm } from '~/types';
import type { TaxableValueOverrides } from '~/types/overrides';
import type { DateRecordsMapByPaymentDate } from '~/types/project';
import { parseFormattedNumber } from '~/utils/numberParsing';
import { findLatestTaxYear } from '~/utils/project-timeline';

// =============================================================================
// GROWTH RATE CALCULATIONS
// =============================================================================

/**
 * Calculate growth rate for a specific year based on input configuration
 *
 * @param year - Project year (0-based, where 0 is starting year)
 * @param inputs - Tax value inputs with growth rate configuration
 * @returns Growth rate as decimal (e.g., 0.03 for 3%)
 */
export function calculateGrowthRateForYear(year: number, inputs: TaxValueInputs): number {
  if (year === 0) return inputs.startingYearGrowthRate;
  if (year <= 5) return inputs.fiveYearRate;
  return inputs.twentyYearRate;
}

/**
 * Calculate compound growth over multiple years
 *
 * @param initialValue - Starting value
 * @param growthRate - Annual growth rate as decimal
 * @param years - Number of years to project
 * @returns Final value after compound growth
 */
export function calculateCompoundGrowth(
  initialValue: number,
  growthRate: number,
  years: number
): number {
  return initialValue * (1 + growthRate) ** years;
}

// =============================================================================
// INPUT VALIDATION UTILITIES
// =============================================================================

/**
 * Check if all required tax value form inputs are complete
 *
 * @param inputs - Tax value form inputs
 * @returns True if all required fields have values greater than 0
 */
export function hasCompleteInputs(inputs: TaxValueInputsForm): boolean {
  // Check that all fields are non-empty
  if (
    inputs.initialTaxableValue.trim() === '' ||
    inputs.startYearGrowthRate.trim() === '' ||
    inputs.fiveYearGrowthRate.trim() === '' ||
    inputs.twentyYearGrowthRate.trim() === ''
  ) {
    return false;
  }

  // Parse and validate that values are > 0
  try {
    const initialTaxableValue = parseFormattedNumber(inputs.initialTaxableValue);
    const startYearGrowthRate = parseFloat(inputs.startYearGrowthRate);
    const fiveYearGrowthRate = parseFloat(inputs.fiveYearGrowthRate);
    const twentyYearGrowthRate = parseFloat(inputs.twentyYearGrowthRate);

    return (
      initialTaxableValue > 0 &&
      startYearGrowthRate > 0 &&
      fiveYearGrowthRate > 0 &&
      twentyYearGrowthRate > 0 &&
      Number.isFinite(initialTaxableValue) &&
      Number.isFinite(startYearGrowthRate) &&
      Number.isFinite(fiveYearGrowthRate) &&
      Number.isFinite(twentyYearGrowthRate)
    );
  } catch {
    return false;
  }
}

/**
 * Validate growth rate values are within acceptable range
 *
 * @param growthRate - Growth rate as decimal
 * @param fieldName - Name of field for error messages
 * @returns Array of error messages (empty if valid)
 */
export function validateGrowthRate(growthRate: number, fieldName: string): string[] {
  const errors: string[] = [];

  if (growthRate <= 0 || growthRate > 1) {
    errors.push(
      `${fieldName} must be greater than 0 and less than or equal to 1 (greater than 0% and up to 100%)`
    );
  }

  return errors;
}

/**
 * Validate starting taxable value
 *
 * @param value - Starting taxable value
 * @returns Array of error messages (empty if valid)
 */
export function validateStartingValue(value: number): string[] {
  const errors: string[] = [];

  if (value <= 0) {
    errors.push('Starting year taxable value must be greater than 0');
  }

  return errors;
}

// =============================================================================
// PROJECTION LENGTH CALCULATION
// =============================================================================

/**
 * Calculate required projection years based on bond series timeline with safety buffer
 * This ensures taxable value map covers all bond payment dates plus reasonable buffer
 *
 * @param startingTaxYear - Starting tax year
 * @param bondSeries - Array of bond series to analyze (optional for fallback)
 * @param dateRecordsMapByPaymentDate - Date records map (optional for fallback)
 * @param safetyBufferYears - Additional years beyond latest bond (default 5)
 * @param minimumYears - Minimum projection years (default 50)
 * @returns Number of years to project
 */
export function calculateRequiredProjectionYears(
  startingTaxYear: number,
  bondSeries?: BondSeries[],
  dateRecordsMapByPaymentDate?: DateRecordsMapByPaymentDate,
  safetyBufferYears: number = 5,
  minimumYears: number = TIME_PERIODS.PROJECT_YEARS
): number {
  // If we have bond data, calculate based on actual timeline
  if (bondSeries && bondSeries.length > 0 && dateRecordsMapByPaymentDate) {
    try {
      const latestTaxYear = findLatestTaxYear(bondSeries, dateRecordsMapByPaymentDate);
      const requiredYears = latestTaxYear - startingTaxYear + safetyBufferYears + 1;

      // Use the larger of calculated years or minimum years
      return Math.max(requiredYears, minimumYears);
    } catch (error) {
      console.warn('Error calculating project timeline, using minimum years:', error);
    }
  }

  // Fallback to minimum years (standard project duration)
  return minimumYears;
}

// =============================================================================
// VALUE PROJECTION UTILITIES
// =============================================================================

/**
 * Project taxable values for a range of years
 *
 * When growth rate overrides are provided, they replace the default rates for specific years.
 * Since values are calculated sequentially using the previous year's value, overriding a year's
 * growth rate creates a cascading effect on all subsequent years.
 *
 * @param inputs - Tax value inputs
 * @param startingTaxYear - Starting tax year
 * @param yearsToProject - Number of years to project (default 50 for safety)
 * @param growthRateOverrides - Optional overrides for specific years' growth rates
 * @returns Array of taxable value map entries
 */
export function projectTaxableValues(
  inputs: TaxValueInputs,
  startingTaxYear: number,
  yearsToProject: number = 50,
  growthRateOverrides?: TaxableValueOverrides | null
): TaxableValueMapEntry[] {
  const results: TaxableValueMapEntry[] = [];
  let currentValue = inputs.startingYearTaxableValue;

  for (let year = 0; year < yearsToProject; year++) {
    const taxYear = startingTaxYear + year;

    // Check for override first, then fall back to default calculation
    const defaultGrowthRate = calculateGrowthRateForYear(year, inputs);
    const overrideEntry = growthRateOverrides?.byYear[taxYear];
    const growthRate = overrideEntry?.overrideGrowthRate ?? defaultGrowthRate;

    // Apply growth rate (except for first year)
    if (year > 0) {
      currentValue *= 1 + growthRate;
    }

    results.push({
      taxYear,
      taxableValue: Math.round(currentValue),
      growthRate,
    });
  }

  return results;
}

/**
 * Get taxable value for a specific tax year
 *
 * @param taxableValueMap - Map or array of taxable values
 * @param taxYear - Target tax year
 * @returns Taxable value for the year or 0 if not found
 */
export function getTaxableValueForYear(
  taxableValueMap: TaxableValueMapEntry[] | Record<number, TaxableValueMapEntry>,
  taxYear: number
): number {
  if (Array.isArray(taxableValueMap)) {
    const entry = taxableValueMap.find((entry) => entry.taxYear === taxYear);
    return entry?.taxableValue || 0;
  } else {
    return taxableValueMap[taxYear]?.taxableValue || 0;
  }
}

// =============================================================================
// SUMMARY AND ANALYSIS UTILITIES
// =============================================================================

/**
 * Calculate summary statistics for taxable value projections
 *
 * @param data - Array of taxable value map entries
 * @returns Summary object or null if no data
 */
export function calculateTaxableValueSummary(data: TaxableValueMapEntry[]) {
  if (data.length === 0) return null;

  const firstYear = data[0];
  const lastYear = data[data.length - 1];
  const totalGrowth = lastYear.taxableValue - firstYear.taxableValue;
  const totalGrowthPercentage = (totalGrowth / firstYear.taxableValue) * 100;

  return {
    startingValue: firstYear.taxableValue,
    endingValue: lastYear.taxableValue,
    totalGrowth,
    totalGrowthPercentage,
    yearSpan: data.length,
    averageAnnualGrowth: totalGrowth / data.length,
    compoundAnnualGrowthRate:
      (lastYear.taxableValue / firstYear.taxableValue) ** (1 / (data.length - 1)) - 1,
  };
}

/**
 * Determine if auto-processing should be triggered based on input completeness
 *
 * @param taxInputs - Tax value form inputs
 * @param hasDateInputs - Whether date inputs are complete
 * @returns True if auto-processing should occur
 */
export function shouldAutoProcess(taxInputs: TaxValueInputsForm, hasDateInputs: boolean): boolean {
  return hasCompleteInputs(taxInputs) && hasDateInputs;
}
